import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from "remotion";

const W = 1080;
const H = 1440;

function seededRand(seed: number): number {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

interface Particle {
  id: number;
  x: number;
  baseY: number;
  size: number;
  speed: number;
  opacity: number;
  phase: number;
}

const RISING: Particle[] = Array.from({ length: 28 }, (_, i) => ({
  id: i,
  x: seededRand(i * 3 + 1) * W,
  baseY: H + seededRand(i * 3 + 2) * H,
  size: 3 + seededRand(i * 3 + 3) * 6,
  speed: 35 + seededRand(i * 3 + 4) * 65,
  opacity: 0.35 + seededRand(i * 3 + 5) * 0.55,
  phase: seededRand(i * 3 + 6) * Math.PI * 2,
}));

const TWINKLE: Particle[] = Array.from({ length: 22 }, (_, i) => ({
  id: i + 100,
  x: seededRand(i * 7 + 10) * W,
  baseY: seededRand(i * 7 + 11) * H * 0.75,
  size: 2 + seededRand(i * 7 + 12) * 4,
  speed: 0,
  opacity: 0.5 + seededRand(i * 7 + 13) * 0.5,
  phase: seededRand(i * 7 + 14) * Math.PI * 2,
}));

export const DevotionalReelOverlay: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const globalFade = interpolate(
    frame,
    [0, Math.round(fps * 0.6), durationInFrames - Math.round(fps * 0.6), durationInFrames],
    [0, 1, 1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  const t = frame / fps;
  const pulse = (Math.sin(t * 1.4) + 1) / 2;
  const pulse2 = (Math.sin(t * 0.9 + 1.2) + 1) / 2;

  return (
    <AbsoluteFill style={{ opacity: globalFade }}>
      {/* Divine light from top center */}
      <div style={{
        position: "absolute",
        top: -80,
        left: "50%",
        transform: "translateX(-50%)",
        width: 900,
        height: 700,
        background: `radial-gradient(ellipse at top, rgba(255, 210, 80, ${0.07 + pulse * 0.07}) 0%, rgba(255, 160, 30, ${0.03 + pulse2 * 0.03}) 40%, transparent 70%)`,
        pointerEvents: "none",
      }} />

      {/* Bottom warmth glow */}
      <div style={{
        position: "absolute",
        bottom: -60,
        left: "50%",
        transform: "translateX(-50%)",
        width: 700,
        height: 400,
        background: `radial-gradient(ellipse at bottom, rgba(255, 130, 20, ${0.04 + pulse2 * 0.04}) 0%, transparent 70%)`,
        pointerEvents: "none",
      }} />

      {/* Rising golden particles */}
      {RISING.map(p => {
        const elapsed = t * p.speed;
        const rawY = p.baseY - elapsed;
        const wrappedY = ((rawY % (H + 300)) + H + 300) % (H + 300) - 300;
        const twinkleAlpha = (Math.sin(frame * 0.06 + p.phase) + 1) / 2;
        const opacity = p.opacity * twinkleAlpha;
        if (opacity < 0.02) return null;
        return (
          <div
            key={p.id}
            style={{
              position: "absolute",
              left: p.x - p.size / 2,
              top: wrappedY - p.size / 2,
              width: p.size,
              height: p.size,
              borderRadius: "50%",
              background: "radial-gradient(circle, #FFF0A0 0%, #FFB800 60%, #FF8C00 100%)",
              opacity,
              boxShadow: `0 0 ${p.size * 2.5}px ${p.size * 1.2}px rgba(255, 180, 30, 0.35)`,
            }}
          />
        );
      })}

      {/* Twinkling sparkles — scattered across image */}
      {TWINKLE.map(p => {
        const alpha = ((Math.sin(frame * 0.07 + p.phase) + 1) / 2) * p.opacity * 0.75;
        if (alpha < 0.02) return null;
        return (
          <div
            key={p.id}
            style={{
              position: "absolute",
              left: p.x - p.size / 2,
              top: p.baseY - p.size / 2,
              width: p.size,
              height: p.size,
              borderRadius: "50%",
              background: "#FFF5C0",
              opacity: alpha,
              boxShadow: `0 0 ${p.size * 4}px rgba(255, 230, 80, 0.7)`,
            }}
          />
        );
      })}

      {/* Pulsing golden border frame */}
      <div style={{
        position: "absolute",
        inset: 0,
        border: `${8 + pulse * 5}px solid rgba(255, 195, 40, ${0.3 + pulse * 0.35})`,
        boxShadow: [
          `inset 0 0 80px rgba(255, 160, 0, ${pulse * 0.18})`,
          `inset 0 0 35px rgba(255, 200, 60, ${pulse * 0.12})`,
          `0 0 50px rgba(255, 160, 0, ${pulse * 0.25})`,
          `0 0 20px rgba(255, 200, 60, ${pulse2 * 0.2})`,
        ].join(", "),
        pointerEvents: "none",
      }} />

      {/* Corner accent glows */}
      {[
        { top: 0, left: 0 },
        { top: 0, right: 0 },
        { bottom: 0, left: 0 },
        { bottom: 0, right: 0 },
      ].map((pos, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            ...pos,
            width: 120,
            height: 120,
            background: `radial-gradient(circle at ${i < 2 ? (i === 0 ? "top left" : "top right") : (i === 2 ? "bottom left" : "bottom right")}, rgba(255, 200, 60, ${(pulse + pulse2) / 2 * 0.18}) 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
      ))}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
